import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // Seed products first
        await axios.post(`${API}/seed-products`);
        // Get featured products
        const res = await axios.get(`${API}/products?featured=true`);
        setFeaturedProducts(res.data.slice(0, 8));
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const categories = [
    { name: 'Makeup', slug: 'makeup', image: 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=600' },
    { name: 'Jewelry', slug: 'jewelry', image: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=600' },
    { name: 'Body', slug: 'body', image: 'https://images.unsplash.com/photo-1571875257727-256c39da42af?w=600' },
    { name: 'Nails', slug: 'nails', image: 'https://images.unsplash.com/photo-1604654894610-df63bc536371?w=600' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=1920"
            alt="Hero"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/50 to-background" />
        </div>

        {/* Content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-flex items-center gap-2 text-gold text-xs font-accent tracking-[0.3em] uppercase mb-6">
              <Sparkles className="h-4 w-4" />
              Luxury Beauty
              <Sparkles className="h-4 w-4" />
            </span>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tight mb-6" data-testid="hero-title">
              <span className="text-gold-gradient">Unbothered</span>
              <br />
              <span className="italic text-foreground">Baddie</span>
            </h1>
            <p className="text-muted-foreground text-base md:text-lg max-w-xl mx-auto mb-10 leading-relaxed">
              Curated luxury beauty essentials and accessories for the confident, unapologetic woman.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                className="bg-[#D4AF37] text-black hover:bg-[#B5952F] px-10 py-6 text-xs tracking-widest uppercase btn-shine font-medium"
                data-testid="shop-now-btn"
              >
                <Link to="/shop">
                  Shop Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black px-10 py-6 text-xs tracking-widest uppercase"
              >
                <Link to="/shop?featured=true">View Collection</Link>
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        >
          <div className="w-px h-16 bg-gradient-to-b from-gold to-transparent" />
        </motion.div>
      </section>

      {/* Categories */}
      <section className="py-24 px-6 md:px-12 bg-background">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl mb-4">
              Shop by <span className="italic text-gold">Category</span>
            </h2>
            <p className="text-muted-foreground">Find your perfect look</p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {categories.map((cat, i) => (
              <motion.div
                key={cat.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Link
                  to={`/shop/${cat.slug}`}
                  className="group relative block aspect-[3/4] overflow-hidden"
                  data-testid={`category-${cat.slug}`}
                >
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover product-image-hover"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-colors duration-300" />
                  <div className="absolute inset-0 flex items-end p-6">
                    <h3 className="font-heading text-xl text-white group-hover:text-gold transition-colors">
                      {cat.name}
                    </h3>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 px-6 md:px-12 bg-card grain-overlay relative">
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="flex justify-between items-end mb-16"
          >
            <div>
              <h2 className="font-heading text-3xl md:text-4xl mb-4">
                Featured <span className="italic text-gold">Picks</span>
              </h2>
              <p className="text-muted-foreground">Our most-loved essentials</p>
            </div>
            <Link
              to="/shop"
              className="hidden md:flex items-center text-sm text-gold hover:text-gold-light transition-colors underline-animate"
            >
              View All
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-secondary/30 mb-4" />
                  <div className="h-4 bg-secondary/30 w-3/4 mb-2" />
                  <div className="h-4 bg-secondary/30 w-1/4" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="featured-products">
              {featuredProducts.map((product, i) => (
                <ProductCard key={product.id} product={product} index={i} />
              ))}
            </div>
          )}

          <Link
            to="/shop"
            className="mt-10 flex md:hidden items-center justify-center text-sm text-gold hover:text-gold-light transition-colors"
          >
            View All Products
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Banner */}
      <section className="py-24 px-6 md:px-12 bg-background">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative overflow-hidden bg-gold/10 border border-gold/20 p-12 md:p-20"
          >
            <div className="relative z-10 text-center max-w-2xl mx-auto">
              <h3 className="font-heading text-3xl md:text-4xl mb-4">
                Be <span className="italic">Unbothered</span>
              </h3>
              <p className="text-muted-foreground mb-8">
                Join our community and get exclusive access to new drops, sales, and baddie tips.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 bg-transparent border-b border-white/20 px-0 py-3 text-sm focus:outline-none focus:border-gold placeholder:text-white/30"
                />
                <Button className="bg-gold text-black hover:bg-gold-dark px-8 py-3 text-xs tracking-widest uppercase">
                  Subscribe
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
